import React, { FunctionComponent, useCallback, useContext, useRef, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { Platform, Pressable, StyleSheet, TextStyle, TouchableOpacity, View } from 'react-native'
import Markdown, { RenderFunction } from 'react-native-markdown-display'

import BottomSheetGorhom from '@gorhom/bottom-sheet'
import * as Linking from 'expo-linking'
import * as WebBrowser from 'expo-web-browser'
import styled, { useTheme } from 'styled-components/native'

import {
  InfoPopoverPlacement,
  InfoPopoverTriggerAction,
  ModalContentConfig,
  TitleInfoPopover,
} from '@lyrahealth-inc/shared-app-logic'

import { AccessibilityRolesNative, IS_WEB } from '../../constants'
import { AppContext } from '../../context'
import { GoalExampleView } from '../../organisms/goalExampleView/GoalExampleView'
import { Modal } from '../../organisms/modal/Modal'
import { RoiRecordsInfoView } from '../../organisms/roiRecordsInfoView/RoiRecordsInfoView'
import { getLinkStyle } from '../../styles/commonStyles'
import { getTypeStyles, moderatBold } from '../../styles/typeStyles'
import { tID } from '../../utils'
import { BodyText, Size as BodyTextSize } from '../bodyText/BodyText'
import { QuestionMarkIcon } from '../icons'
import { InfoIcon } from '../icons/InfoIcon'
import { InfoPopover } from '../infoPopover/InfoPopover'
import { Link } from '../link/Link'
import { TextButton } from '../textButton/TextButton'
import { TOOLTIP_TRIGGER_ICON } from '../tooltip/Tooltip'

export type StyledMarkdownProps = {
  content?: string
  hasError?: boolean
  center?: boolean
  customStyles?: Dict
  linkInfoPopover?: TitleInfoPopover
  modalContentInfo?: ModalContentConfig
  handleVideoLinkOpen?: (url: string) => Promise<void>
  onPopOverPress?: (link: string) => void
}

const handleLinkOpen = async (
  url: string,
  handleVideoLinkOpen?: (url: string) => Promise<void>,
  customerName?: string,
  domain?: string,
) => {
  const supported = await Linking.canOpenURL(url)
  if (supported && url.indexOf('https://') === 0) {
    if (url.startsWith('https://player.vimeo.com') && handleVideoLinkOpen !== undefined) {
      handleVideoLinkOpen(url)
    } else if (IS_WEB) {
      window.open(url, '_blank')
    } else {
      WebBrowser.openBrowserAsync(url)
    }
  } else if (customerName !== undefined && domain !== undefined) {
    // If link cannot be opened, try an internal LW link
    WebBrowser.openBrowserAsync(`https://${customerName}.${domain}${url}`)
  } else {
    console.warn({
      unsupported_link: `attempted to navigate to an unsupported url from a markdown link: ${url}`,
    })
  }
}

const getHeaderRenderFunction = (style: string, level?: number): RenderFunction => {
  return (node, children, _parent, styles) => {
    return (
      <View
        accessible
        key={node.key}
        style={styles[style]}
        accessibilityRole={level ? AccessibilityRolesNative.HEADER : undefined}
        {...(IS_WEB && level && { 'aria-level': level })}
      >
        {children}
      </View>
    )
  }
}

const getBulletListRenderFunction = (): RenderFunction => {
  return (node, children, _parent, styles) => (
    <React.Fragment key={node.key}>
      {children.map((child: any, index: number) => {
        if (index === children.length - 1) {
          return React.cloneElement(child, {
            key: child.key,
            style: [child.props.style, styles.lastListItem],
          })
        }
        return child
      })}
    </React.Fragment>
  )
}

const getLinkRenderFunction = (style: TextStyle): RenderFunction => {
  // We need to access the AppContext to get the customer name and domain
  const { customerName, domain } = useContext(AppContext)
  return (node, children) => {
    // Clean up the URL - remove any {{new_tab}} or similar markers
    let url = node?.attributes?.href || ''
    url = url.replace(/{{.*?}}/g, '')
    return (
      <Link
        style={style}
        text={children}
        onPress={() => {
          // For "/faq" links, use a special handler
          if (url.startsWith('/faq')) {
            // Construct the FAQ URL based on environment and customer name
            let faqUrl = ''

            if (customerName) {
              // Use customer name with appropriate domain based on environment
              // Add redirect parameter to ensure user is redirected to FAQ after login
              faqUrl = `https://${customerName}.${domain}/faq`
            }

            if (IS_WEB) {
              // On web, open in a new tab
              window.open(faqUrl, '_blank')
            } else {
              // On mobile, try Linking first
              Linking.openURL(faqUrl)
                .then(() => {})
                .catch((_error) => {
                  // Fallback to WebBrowser
                  WebBrowser.openBrowserAsync(faqUrl).catch((_err) => {})
                })
            }
          } else if (IS_WEB && url) {
            window.open(url, '_blank')
          } else if (url) {
            WebBrowser.openBrowserAsync(url)
          }
        }}
        containerStyle={{ display: 'inline' }}
      />
    )
  }
}

// This is needed so that popover text doesn't get cut off on iOS
const PaddedContentView = styled(View)({
  paddingBottom: Platform.OS === 'ios' ? 10 : 0,
})

const TriggerContainer = styled(View)({
  marginBottom: -6,
})

const getTooltipRenderFunction = ({
  linkInfoPopover,
  formatMessage,
  handleVideoLinkOpen,
  onPopOverPress,
  customerName,
  domain,
  isPopoverOpen,
  setIsPopoverOpen,
}: {
  linkInfoPopover: TitleInfoPopover
  formatMessage: IntlShape['formatMessage']
  handleVideoLinkOpen?: (url: string) => Promise<void>
  onPopOverPress?: (link: string) => void
  customerName?: string
  domain?: string
  isPopoverOpen: boolean
  setIsPopoverOpen: (isPopoverOpen: boolean) => void
}): RenderFunction => {
  const header = <BodyText text={linkInfoPopover.header || linkInfoPopover.content} size={BodyTextSize.DEFAULT} />
  const content = linkInfoPopover.header ? (
    <BodyText text={linkInfoPopover.content} size={BodyTextSize.SMALL} />
  ) : undefined
  let footer: React.ReactElement | undefined
  if (linkInfoPopover.footer) {
    if (linkInfoPopover.linkType) {
      footer = (
        <Link
          text={linkInfoPopover.footer}
          underline
          onPress={() => {
            const link = (IS_WEB ? linkInfoPopover.webLink : linkInfoPopover.mobileLink) || ''
            if (linkInfoPopover.linkType === 'internal') {
              onPopOverPress && onPopOverPress(link)
            } else if (linkInfoPopover.linkType === 'external') {
              handleLinkOpen(link, handleVideoLinkOpen, customerName, domain)
            }
          }}
        />
      )
    } else {
      footer = linkInfoPopover.footer ? <BodyText text={linkInfoPopover.footer} size={BodyTextSize.SMALL} /> : undefined
    }
  }
  return (node, children, _parent, _styles) => (
    <InfoPopover
      key={node.key}
      placement={linkInfoPopover?.placement || InfoPopoverPlacement.TOP}
      header={header}
      content={<PaddedContentView>{content}</PaddedContentView>}
      footerContent={footer}
      isPopoverOpen={isPopoverOpen}
      setIsPopoverOpen={setIsPopoverOpen}
      trigger={
        <TouchableOpacity
          testID={tID('popover-button')}
          accessibilityLabel={
            linkInfoPopover.accessibilityLabel ||
            formatMessage({
              defaultMessage: 'Tooltip',
              description: 'Tooltip for more information',
            })
          }
        >
          <TriggerContainer>
            {linkInfoPopover.icon ? (
              linkInfoPopover.icon === TOOLTIP_TRIGGER_ICON.INFO ? (
                <InfoIcon size={16} />
              ) : (
                <QuestionMarkIcon width={16} />
              )
            ) : (
              <BodyText text={children} />
            )}
          </TriggerContainer>
        </TouchableOpacity>
      }
      animationExitDuration={Platform.OS === 'android' ? 50 : 500} // animation is janky on Android
      mode={Platform.OS === 'web' ? 'single' : 'multiple'}
      popoverTriggerAction={linkInfoPopover?.popoverTriggerAction || InfoPopoverTriggerAction.PRESS}
    />
  )
}

/**
 * This component is special in that it accesses the secure store from the
 * consuming app and uses the customerName and domain set on the device to create the markdown component
 */
export const StyledMarkdown: FunctionComponent<StyledMarkdownProps> = ({
  content = '',
  hasError,
  center,
  customStyles,
  linkInfoPopover,
  modalContentInfo,
  handleVideoLinkOpen,
  onPopOverPress,
}) => {
  const { formatMessage } = useIntl()
  const { customerName, domain } = useContext(AppContext)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false)
  const { colors, breakpoints, fontFamilyMetadata } = useTheme()
  const bottomSheetRef = useRef<BottomSheetGorhom>(null)
  const openBottomSheet = useCallback(() => {
    bottomSheetRef?.current?.expand()
  }, [])
  const closeBottomSheet = useCallback(() => {
    bottomSheetRef?.current?.close()
    setIsModalOpen(false)
  }, [setIsModalOpen])

  const getModalRenderFunction = () => {
    return (node: any, children: any, _parent: any, _styles: any) => {
      return <TextButton key={node.key} text={children} onPress={() => setIsModalOpen(true)} />
    }
  }

  const { componentShown = '' } = modalContentInfo ?? {}
  // views from provider portal that should not have/can't have fullscreenmodal and bottomsheet
  const noFullScreenModal = ['goalExampleView']
  const disableFullScreenModal = noFullScreenModal.includes(componentShown)

  const getModalContent = (componentShown: string) => {
    switch (componentShown) {
      case 'goalExampleView':
        return <GoalExampleView onClosePress={() => setIsModalOpen(false)} />
      case 'roiRecordsInfoView':
        return <RoiRecordsInfoView onClosePress={() => setIsModalOpen(false)} />
    }
    return <></>
  }

  const modalComponent = modalContentInfo ? (
    <Modal
      onLayout={openBottomSheet}
      onRequestClose={() => {
        setIsModalOpen(false)
      }}
      visible={isModalOpen}
      onCloseEnd={() => {
        if (breakpoints?.isMobileSized) {
          closeBottomSheet()
        }
        setIsModalOpen(false)
      }}
      bottomSheetRef={bottomSheetRef}
      showCloseIcon={false}
      useDynamicHeightForBottomSheet
      disableFullScreenModal={disableFullScreenModal}
      disableBottomSheet={disableFullScreenModal}
      modalContents={getModalContent(componentShown)}
    />
  ) : null

  const onLinkPress = (url: string) => {
    // Removes "{{new_tab}}" from url which is used on web to designate link targets
    const linkWithoutTabAttribute = url.replace(/ *%7B[^)]*%7D */g, '')
    handleLinkOpen(linkWithoutTabAttribute, handleVideoLinkOpen, customerName, domain)
    // Returning false prevents Markdown from opening link
    return false
  }

  const errorStyles = hasError ? { color: colors.textError, textAlign: 'left' } : {}
  const customStylesheet = StyleSheet.create({
    h1: { ...(customStyles?.h1 || {}), ...errorStyles },
    h2: { ...(customStyles?.h2 || {}), ...errorStyles },
    h3: { ...(customStyles?.h3 || {}), ...errorStyles },
    h4: { ...(customStyles?.h4 || {}), ...errorStyles },
    h5: { ...(customStyles?.h5 || {}), ...errorStyles },
    b1: { ...(customStyles?.b1 || {}), ...errorStyles },
    p1: { ...(customStyles?.p1 || {}), ...errorStyles },
    p2: { ...(customStyles?.p2 || {}), ...errorStyles },
    p3: { ...(customStyles?.p3 || {}), ...errorStyles },
    em: { ...(customStyles?.em || {}), ...errorStyles },
  } as { [key: string]: TextStyle })

  const typeStyles = getTypeStyles(colors, fontFamilyMetadata)

  const elements: { [key: string]: TextStyle } = {
    h1: typeStyles.headlineMedium,
    h2: typeStyles.headlineSmall,
    h3: typeStyles.subheadLarge,
    h4: { ...typeStyles.subheadMedium, lineHeight: 26.4 },
    h5: typeStyles.subheadSmall,
    b1: typeStyles.bodyLarge,
    p1: typeStyles.subheadXsmall,
    p2: typeStyles.bodyDefault,
    p3: typeStyles.bodyDefault,
    em: typeStyles.em,
  }

  const elementStyles = Object.keys(elements).reduce<{ [key: string]: TextStyle }>((acc, key) => {
    acc[key] = { ...elements[key], ...customStylesheet[key] }
    return acc
  }, {})

  const styles = {
    /* stylelint-disable -- external Markdown lib will handle react native styling */
    heading1: elementStyles.h1,
    heading2: elementStyles.h2,
    heading3: elementStyles.h3,
    heading4: elementStyles.h4,
    heading5: elementStyles.b1,
    heading6: elementStyles.p1,
    paragraph: elementStyles.p2,
    list_item: elementStyles.p2,
    lastListItem: elementStyles.p3,
    link: { ...getLinkStyle(colors, fontFamilyMetadata).link, ...(customStyles?.link || {}) },
    em: elementStyles.em,
    strong: moderatBold as TextStyle,
    textgroup: center ? textGroupCentered.textGroupCentered : {},
    body: { flexShrink: 1 },
    // This is a hack to ensure that markdown interpreted as list content renders on Native
    // https://github.com/iamacup/react-native-markdown-display/issues/218
    // https://lyrahealth.atlassian.net/browse/ICE-4697
    ordered_list_content: {
      ...(!IS_WEB && { flex: undefined, flexShrink: 1 }),
    },
    bullet_list_content: {
      ...(!IS_WEB && { flex: undefined, flexShrink: 1 }),
    },
  }

  /**
   * Custom heading rules added for accessibility.
   * See https://github.com/iamacup/react-native-markdown-display/blob/master/src/lib/renderRules.js
   * Having to add bullet_list to handle edge case of when list is the last item in a section as that
   *  is messing up new line margin
   */
  const rules: { [key: string]: RenderFunction } = {
    heading1: getHeaderRenderFunction('_VIEW_SAFE_heading1', 1),
    heading2: getHeaderRenderFunction('_VIEW_SAFE_heading2', 2),
    heading3: getHeaderRenderFunction('_VIEW_SAFE_heading3', 3),
    heading4: getHeaderRenderFunction('_VIEW_SAFE_heading4', 4),
    heading5: getHeaderRenderFunction('_VIEW_SAFE_heading5'),
    heading6: getHeaderRenderFunction('_VIEW_SAFE_heading6'),
    bullet_list: getBulletListRenderFunction(),
    link: getLinkRenderFunction(styles.link),
    ...(linkInfoPopover && {
      link: getTooltipRenderFunction({
        linkInfoPopover,
        formatMessage,
        handleVideoLinkOpen,
        onPopOverPress,
        customerName,
        domain,
        isPopoverOpen,
        setIsPopoverOpen,
      }),
    }),
    ...(modalContentInfo && {
      link: getModalRenderFunction(),
    }),
  }

  const markdownComponent = (
    <Markdown onLinkPress={onLinkPress} style={styles} rules={rules}>
      {content}
    </Markdown>
  )

  // On mobile, wrap the text in a pressable so that the info popover is accessible
  // It is unaccessible otherwise since it is within a text element

  return (
    <>
      {Platform.OS !== 'web' && linkInfoPopover ? (
        <Pressable
          onPress={() => setIsPopoverOpen((prev) => !prev)}
          accessible={true}
          accessibilityRole='button'
          accessibilityLabel={linkInfoPopover.accessibilityLabel ?? content}
        >
          {markdownComponent}
        </Pressable>
      ) : (
        markdownComponent
      )}
      {modalComponent}
    </>
  )
}

const textGroupCentered = StyleSheet.create({
  textGroupCentered: { textAlign: 'center' },
})
